import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Trash2, Copy, Building2, Home, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';
import ClientSearchBar from './ClientSearchBar';
import SdrAvailabilityPicker from './SdrAvailabilityPicker';
import RecurrencePicker from './RecurrencePicker';
import DateRangeSelector from './DateRangeSelector';
import ValidationSummary from './ValidationSummary';

type ModalMode = 'create' | 'edit';
type RecurrenceMode = 'none' | 'weekly' | 'manual';

interface Client {
  id: string;
  name: string;
  company_name: string;
}

interface Squad {
  id: string;
  name: string;
  color: string;
}

interface Sdr {
  user_id: string;
  sales_name: string | null;
  email: string | null;
  squad_id: string | null;
}

interface Schedule {
  id?: string;
  sdr_id: string | null;
  client_id: string | null;
  squad_id: string | null;
  date: string;
  day_type: string;
  status: string;
  work_location?: string | null;
}

interface UnifiedAgendaModalProps {
  mode: ModalMode;
  initialSchedule?: Schedule | null;
  initialDate?: string;
  clients: Client[];
  sdrs: Sdr[];
  squads: Squad[];
  schedules: Schedule[];
  preSelectedSdr?: string;
  preSelectedClient?: string;
  onClose: () => void;
  onSave: () => void;
}

const UnifiedAgendaModal: React.FC<UnifiedAgendaModalProps> = ({
  mode,
  initialSchedule,
  initialDate,
  clients,
  sdrs,
  squads,
  schedules,
  preSelectedSdr = '',
  preSelectedClient = '',
  onClose,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState<'form' | 'validation'>('form');
  const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>('none');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [multiSdrMode, setMultiSdrMode] = useState(false);

  const [formData, setFormData] = useState({
    client_id: initialSchedule?.client_id || preSelectedClient || '',
    sdr_id: initialSchedule?.sdr_id || preSelectedSdr || '',
    sdr_ids: [] as string[],
    base_date: initialSchedule?.date || initialDate || new Date().toISOString().split('T')[0],
    day_type: initialSchedule?.day_type || 'prospection',
    status: initialSchedule?.status || 'planned',
    work_location: (initialSchedule?.work_location || null) as string | null
  });

  const [isCustomDayType, setIsCustomDayType] = useState(
    initialSchedule ? !['prospection', 'congé', 'malade', 'férié', 'formation', 'Jour Off'].includes(initialSchedule.day_type) : false
  );

  const [weeklyConfig, setWeeklyConfig] = useState({ occurrences: 4 });
  const [manualDates, setManualDates] = useState<string[]>([]);

  const sdrOccupiedDates = useMemo(() => {
    if (multiSdrMode) {
      return [];
    }
    if (!formData.sdr_id) return [];

    return schedules
      .filter(s => s.sdr_id === formData.sdr_id && s.id !== initialSchedule?.id)
      .map(s => s.date);
  }, [formData.sdr_id, schedules, initialSchedule, multiSdrMode]);

  const selectedDates = useMemo(() => {
    if (recurrenceMode === 'none') {
      return [formData.base_date];
    } else if (recurrenceMode === 'weekly') {
      const dates: string[] = [];
      const base = new Date(formData.base_date);
      for (let i = 0; i < weeklyConfig.occurrences; i++) {
        const nextDate = new Date(base);
        nextDate.setDate(base.getDate() + (i * 7));
        dates.push(nextDate.toISOString().split('T')[0]);
      }
      return dates;
    } else {
      return manualDates.length > 0 ? manualDates : [formData.base_date];
    }
  }, [recurrenceMode, formData.base_date, weeklyConfig.occurrences, manualDates]);

  const validationErrors = useMemo(() => {
    const errors: Array<{ type: 'error' | 'warning'; message: string }> = [];

    if (formData.day_type === 'prospection') {
      if (!formData.client_id) {
        errors.push({ type: 'error', message: 'Un client est requis pour une journée de prospection' });
      }
      if (multiSdrMode) {
        if (formData.sdr_ids.length === 0) {
          errors.push({ type: 'error', message: 'Au moins un SDR est requis pour une journée de prospection' });
        }
      } else {
        if (!formData.sdr_id) {
          errors.push({ type: 'error', message: 'Un SDR est requis pour une journée de prospection' });
        }
      }
    }

    if (['congé', 'malade', 'Jour Off'].includes(formData.day_type) && !formData.sdr_id) {
      errors.push({ type: 'error', message: `Un SDR est requis pour un ${formData.day_type}` });
    }

    if (isCustomDayType) {
      if (!formData.day_type || formData.day_type.trim() === '') {
        errors.push({ type: 'error', message: 'Un nom est requis pour un type de journée personnalisé' });
      }
      if (!formData.sdr_id) {
        errors.push({ type: 'error', message: 'Un SDR est requis pour un type de journée personnalisé' });
      }
    }

    if (selectedDates.length === 0) {
      errors.push({ type: 'error', message: 'Au moins une date doit être sélectionnée' });
    }

    if (!multiSdrMode && formData.sdr_id && (['prospection', 'congé', 'malade', 'Jour Off'].includes(formData.day_type) || isCustomDayType)) {
      const conflicts = selectedDates.filter(date => {
        return schedules.some(s =>
          s.sdr_id === formData.sdr_id &&
          s.date === date &&
          s.id !== initialSchedule?.id
        );
      });

      if (conflicts.length > 0) {
        errors.push({
          type: 'warning',
          message: `Le SDR sélectionné a déjà ${conflicts.length} assignation${conflicts.length > 1 ? 's' : ''} sur les dates choisies`
        });
      }
    }

    if (formData.client_id && formData.day_type === 'prospection') {
      const duplicateClients = selectedDates.filter(date => {
        return schedules.some(s =>
          s.client_id === formData.client_id &&
          s.date === date &&
          s.id !== initialSchedule?.id
        );
      });

      if (duplicateClients.length > 0) {
        errors.push({
          type: 'warning',
          message: `Ce client est déjà assigné sur ${duplicateClients.length} date${duplicateClients.length > 1 ? 's' : ''} sélectionnée${duplicateClients.length > 1 ? 's' : ''}`
        });
      }
    }

    return errors;
  }, [formData, selectedDates, schedules, initialSchedule, multiSdrMode]);

  const handleSubmit = async () => {
    if (validationErrors.some(e => e.type === 'error')) {
      return;
    }

    if (mode === 'edit' && initialSchedule) {
      await handleUpdate();
    } else {
      await handleCreate();
    }
  };

  const handleCreate = async () => {
    setIsSaving(true);
    setError(null);

    try {
      let datesToInsert: any[] = [];

      if (['férié', 'formation'].includes(formData.day_type)) {
        datesToInsert = selectedDates.flatMap(date =>
          sdrs.map(sdr => ({
            client_id: null,
            sdr_id: sdr.user_id,
            date: date,
            day_type: formData.day_type,
            status: formData.status,
            squad_id: sdr.squad_id || null,
            work_location: formData.work_location
          }))
        );
      } else if (multiSdrMode && formData.day_type === 'prospection') {
        datesToInsert = selectedDates.flatMap(date =>
          formData.sdr_ids.map(sdrId => {
            const selectedSdr = sdrs.find(sdr => sdr.user_id === sdrId);
            return {
              client_id: formData.client_id || null,
              sdr_id: sdrId,
              date: date,
              day_type: formData.day_type,
              status: formData.status,
              squad_id: selectedSdr?.squad_id || null,
              work_location: formData.work_location
            };
          })
        );
      } else {
        const selectedSdr = sdrs.find(sdr => sdr.user_id === formData.sdr_id);
        datesToInsert = selectedDates.map(date => ({
          client_id: formData.client_id || null,
          sdr_id: formData.sdr_id || null,
          date: date,
          day_type: formData.day_type,
          status: formData.status,
          squad_id: selectedSdr?.squad_id || null,
          work_location: formData.work_location
        }));
      }

      const { error } = await supabase
        .from('prospecting_schedule')
        .insert(datesToInsert);

      if (error) throw error;

      await onSave();
      onClose();
    } catch (error: any) {
      console.error('Error creating schedule:', error);
      setError(error.message || 'Une erreur est survenue lors de la création');
      setCurrentStep('form');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!initialSchedule) return;

    setIsSaving(true);
    setError(null);

    try {
      const selectedSdr = sdrs.find(sdr => sdr.user_id === formData.sdr_id);

      const { error } = await supabase
        .from('prospecting_schedule')
        .update({
          client_id: formData.client_id || null,
          sdr_id: formData.sdr_id || null,
          date: formData.base_date,
          day_type: formData.day_type,
          status: formData.status,
          squad_id: selectedSdr?.squad_id || null,
          work_location: formData.work_location
        })
        .eq('id', initialSchedule.id);

      if (error) throw error;

      await onSave();
      onClose();
    } catch (error: any) {
      console.error('Error updating schedule:', error);
      setError(error.message || 'Une erreur est survenue lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!initialSchedule || !confirm('Voulez-vous vraiment supprimer cette journée ?')) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('prospecting_schedule')
        .delete()
        .eq('id', initialSchedule.id);

      if (error) throw error;

      await onSave();
      onClose();
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      setError(error.message || 'Une erreur est survenue lors de la suppression');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = () => {
    setFormData(prev => ({
      ...prev,
      base_date: new Date().toISOString().split('T')[0]
    }));
  };

  const handleValidate = () => {
    if (validationErrors.some(e => e.type === 'error')) {
      return;
    }
    setCurrentStep('validation');
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700 max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gray-800 border-b border-gray-700 p-6 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {mode === 'edit' ? 'Modifier la journée' : 'Créer des journées'}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {currentStep === 'validation'
                  ? 'Vérifiez les informations avant de créer'
                  : mode === 'edit'
                    ? 'Modifiez les informations de la journée'
                    : 'Remplissez les informations pour créer une ou plusieurs journées'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <AnimatePresence mode="wait">
              {currentStep === 'form' ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-6"
                >
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Étape 1 : Type de journée</h3>

                    {!isCustomDayType ? (
                      <>
                        <select
                          value={formData.day_type}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setIsCustomDayType(true);
                              setFormData({
                                ...formData,
                                day_type: '',
                                client_id: '',
                                sdr_id: ''
                              });
                            } else {
                              setFormData({
                                ...formData,
                                day_type: e.target.value,
                                client_id: e.target.value === 'prospection' ? formData.client_id : '',
                                sdr_id: ['prospection', 'congé', 'malade', 'Jour Off'].includes(e.target.value) ? formData.sdr_id : ''
                              });
                            }
                          }}
                          disabled={isSaving}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="prospection">Prospection (Client + SDR)</option>
                          <option value="congé">Congé (SDR uniquement)</option>
                          <option value="malade">Maladie (SDR uniquement)</option>
                          <option value="Jour Off">Jour Off (SDR uniquement)</option>
                          <option value="férié">Férié (Tous les SDR)</option>
                          <option value="formation">Formation (Tous les SDR, sauf prospection)</option>
                          <option value="__custom__">Autre (Personnalisé)</option>
                        </select>
                        <p className="text-xs text-gray-400 mt-2">
                          {formData.day_type === 'prospection' && '→ Nécessite un client et un SDR'}
                          {formData.day_type === 'congé' && '→ Nécessite uniquement un SDR'}
                          {formData.day_type === 'malade' && '→ Nécessite uniquement un SDR'}
                          {formData.day_type === 'Jour Off' && '→ Nécessite uniquement un SDR'}
                          {formData.day_type === 'férié' && '→ Bloque tous les SDR, pas de prospection possible'}
                          {formData.day_type === 'formation' && '→ Bloque tous les SDR par défaut, mais prospection possible'}
                        </p>
                      </>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Nom du type de journée <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.day_type}
                            onChange={(e) => setFormData({ ...formData, day_type: e.target.value })}
                            disabled={isSaving}
                            placeholder="Ex: Séminaire, Réunion équipe, Team building..."
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-400 mt-2">
                            Ce nom sera affiché dans le planning pour identifier cette journée
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setIsCustomDayType(false);
                            setFormData({ ...formData, day_type: 'prospection' });
                          }}
                          disabled={isSaving}
                          className="text-sm text-blue-400 hover:text-blue-300 underline disabled:opacity-50"
                        >
                          ← Revenir aux types prédéfinis
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-green-400 mb-3">
                      Étape 2 : {mode === 'create' ? 'Choisissez les dates' : 'Date de la journée'}
                    </h3>

                    {mode === 'create' && (
                      <RecurrencePicker
                        mode={recurrenceMode}
                        onModeChange={setRecurrenceMode}
                        baseDate={formData.base_date}
                        weeklyConfig={weeklyConfig}
                        onWeeklyConfigChange={setWeeklyConfig}
                        disabled={isSaving}
                      />
                    )}

                    {recurrenceMode === 'none' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Date <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="date"
                          value={formData.base_date}
                          onChange={(e) => setFormData({ ...formData, base_date: e.target.value })}
                          disabled={isSaving}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    )}

                    {recurrenceMode === 'manual' && (
                      <div className="mt-4">
                        <DateRangeSelector
                          selectedDates={manualDates}
                          onDatesChange={setManualDates}
                          disabled={isSaving}
                          excludeWeekends={true}
                          occupiedDates={sdrOccupiedDates}
                        />
                      </div>
                    )}
                  </div>

                  {formData.day_type === 'prospection' && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-blue-400 mb-3">Étape 3 : Sélectionnez le client</h3>
                      <ClientSearchBar
                        clients={clients}
                        selectedClientId={formData.client_id}
                        onClientSelect={(clientId) => setFormData({ ...formData, client_id: clientId })}
                        disabled={isSaving}
                        required={true}
                      />
                    </div>
                  )}

                  {(['prospection', 'congé', 'malade', 'Jour Off'].includes(formData.day_type) || isCustomDayType) && (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-purple-400">
                          {formData.day_type === 'prospection' ? 'Étape 4' : 'Étape 3'} : Assignez {multiSdrMode ? 'des SDRs' : 'un SDR'}
                        </h3>
                        {formData.day_type === 'prospection' && mode === 'create' && (
                          <button
                            onClick={() => {
                              setMultiSdrMode(!multiSdrMode);
                              if (!multiSdrMode) {
                                setFormData({ ...formData, sdr_ids: formData.sdr_id ? [formData.sdr_id] : [], sdr_id: '' });
                              } else {
                                setFormData({ ...formData, sdr_id: formData.sdr_ids[0] || '', sdr_ids: [] });
                              }
                            }}
                            className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                          >
                            {multiSdrMode ? 'Mode simple' : 'Mode multiple'}
                          </button>
                        )}
                      </div>
                      <SdrAvailabilityPicker
                        sdrs={sdrs}
                        squads={squads}
                        schedules={schedules}
                        selectedSdrId={formData.sdr_id}
                        selectedSdrIds={formData.sdr_ids}
                        selectedDates={selectedDates}
                        onSdrSelect={(sdrId) => setFormData({ ...formData, sdr_id: sdrId })}
                        onSdrMultiSelect={(sdrIds) => setFormData({ ...formData, sdr_ids: sdrIds })}
                        disabled={isSaving}
                        required={true}
                        multiSelect={multiSdrMode && formData.day_type === 'prospection'}
                      />
                    </div>
                  )}

                  {['férié', 'formation'].includes(formData.day_type) && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                          <span className="text-yellow-400 text-lg">ℹ</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-yellow-400 mb-1">
                            {formData.day_type === 'férié' ? 'Jour férié pour tous' : 'Formation collective'}
                          </h4>
                          <p className="text-xs text-gray-300">
                            {formData.day_type === 'férié'
                              ? 'Cette journée sera bloquée pour tous les SDR. Aucune prospection ne pourra être planifiée.'
                              : 'Cette journée sera créée pour tous les SDR. Des journées de prospection pourront être planifiées individuellement par-dessus.'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-300 mb-3">Lieu de travail</h3>
                    <div className="flex items-center gap-2">
                      {([
                        { value: 'office', label: 'Bureau', icon: Building2 },
                        { value: 'remote', label: 'Télétravail', icon: Home },
                        { value: 'other', label: 'Autre', icon: MapPin },
                      ] as const).map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFormData({ ...formData, work_location: formData.work_location === value ? null : value })}
                          disabled={isSaving}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            formData.work_location === value
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md'
                              : 'bg-gray-800 border border-gray-600 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Optionnel. Cliquer à nouveau pour désélectionner.
                    </p>
                  </div>

                  <div className="bg-gray-700/50 border border-gray-600 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-300 mb-3">Statut</h3>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      disabled={isSaving}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="planned">Planifié</option>
                      <option value="done">Effectué</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="validation"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <ValidationSummary
                    dates={selectedDates}
                    clientId={formData.client_id}
                    sdrId={formData.sdr_id}
                    sdrIds={formData.sdr_ids}
                    clients={clients}
                    sdrs={sdrs}
                    dayType={formData.day_type}
                    errors={validationErrors}
                    onConfirm={handleSubmit}
                    onCancel={() => setCurrentStep('form')}
                    isLoading={isSaving}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {currentStep === 'form' && (
            <div className="bg-gray-800 border-t border-gray-700 p-6 flex items-center justify-between flex-shrink-0">
              <div className="flex space-x-2">
                {mode === 'edit' && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDelete}
                      disabled={isSaving}
                      icon={<Trash2 className="h-4 w-4" />}
                      className="border-red-500 text-red-400 hover:bg-red-500/10"
                    >
                      Supprimer
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDuplicate}
                      disabled={isSaving}
                      icon={<Copy className="h-4 w-4" />}
                    >
                      Dupliquer
                    </Button>
                  </>
                )}
              </div>

              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  Annuler
                </Button>
                {mode === 'edit' ? (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSaving || validationErrors.some(e => e.type === 'error')}
                    icon={<Save className="h-4 w-4" />}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleValidate}
                    disabled={isSaving || validationErrors.some(e => e.type === 'error')}
                    icon={<Save className="h-4 w-4" />}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Valider et créer
                  </Button>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UnifiedAgendaModal;
